/**
 * Owner — ตั้งสายอนุมัติทั้งบริษัท (L1 → L2 → L3)
 * L1 = หัวหน้างานของพนักงานแต่ละคน (ตั้งในหน้าพนักงาน) · L2/L3 = คนที่กำหนดที่นี่ (ใช้ทั้งบริษัท)
 */
import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useApprovalChain, useSetApprovalChain, useSupervisorCandidates } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { Loading } from '../../components/layout/Loading';

export function OwnerApprovalChain() {
  const { data, isLoading } = useApprovalChain();
  const { data: candidates = [] } = useSupervisorCandidates();
  const save = useSetApprovalChain();
  const toast = useToast();

  const [levels, setLevels] = useState(1);
  const [l2, setL2] = useState('');
  const [l3, setL3] = useState('');

  useEffect(() => {
    if (data) {
      setLevels(data.levels);
      setL2(data.l2_emp_code ?? '');
      setL3(data.l3_emp_code ?? '');
    }
  }, [data]);

  async function onSave() {
    if (levels >= 2 && !l2) return toast.error('เลือกผู้อนุมัติชั้น 2');
    if (levels >= 3 && !l3) return toast.error('เลือกผู้อนุมัติชั้น 3');
    try {
      await save.mutateAsync({
        levels,
        l2_emp_code: levels >= 2 ? l2 : null,
        l3_emp_code: levels >= 3 ? l3 : null,
      });
      toast.success('บันทึกสายอนุมัติแล้ว');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  if (isLoading) {
    return (
      <AdminLayout title="สายอนุมัติ">
        <Loading />
      </AdminLayout>
    );
  }

  const picker = (value: string, onChange: (v: string) => void, label: string) => (
    <div>
      <label className="label">{label}</label>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">— เลือกผู้อนุมัติ —</option>
        {candidates.map((c) => (
          <option key={c.emp_code} value={c.emp_code}>
            {c.name}{c.position ? ` · ${c.position}` : ''}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <AdminLayout title="สายอนุมัติ">
      <div className="card p-4 space-y-4 border-brand-200">
        <div>
          <div className="font-semibold mb-1">สายอนุมัติลา / OT / ย้ายวันหยุด (ทั้งบริษัท)</div>
          <p className="text-xs text-gray-500">
            ชั้นแรก (L1) = หัวหน้างานของพนักงานแต่ละคน (ตั้งในหน้าพนักงาน) · ชั้นถัดไปใช้คนเดียวกันทั้งบริษัท ·
            ใช้กับคำขอย้ายวันหยุดด้วย (เฉพาะพนักงานที่มีหัวหน้า — ไม่มีหัวหน้าจะให้ HR/เจ้าของอนุมัติชั้นเดียว)
          </p>
        </div>

        <div>
          <label className="label">จำนวนชั้นอนุมัติ</label>
          <select className="input" value={levels} onChange={(e) => setLevels(Number(e.target.value))}>
            <option value={1}>1 ชั้น — หัวหน้าอนุมัติจบ</option>
            <option value={2}>2 ชั้น — หัวหน้า → ผู้อนุมัติชั้น 2</option>
            <option value={3}>3 ชั้น — หัวหน้า → ชั้น 2 → ชั้น 3</option>
          </select>
        </div>

        {levels >= 2 && picker(l2, setL2, 'ผู้อนุมัติชั้น 2 (L2)')}
        {levels >= 3 && picker(l3, setL3, 'ผู้อนุมัติชั้น 3 (L3)')}

        {candidates.length === 0 && levels >= 2 && (
          <p className="text-xs text-amber-600">
            ยังไม่มีคนที่ตั้งเป็นหัวหน้า/ผู้จัดการ/HR — ไปตั้งสิทธิ์ให้พนักงานก่อน (หน้าพนักงาน → สิทธิ์ใช้งานระบบ) แล้วค่อยกลับมาเลือก
          </p>
        )}

        <button onClick={onSave} disabled={save.isPending} className="btn-primary w-full">
          {save.isPending ? 'กำลังบันทึก…' : 'บันทึกสายอนุมัติ'}
        </button>

        <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-xs text-gray-700">
          ℹ️ เมื่อพนักงานยื่นลา/OT จะวิ่งหา <b>หัวหน้า (L1)</b> ก่อน → อนุมัติแล้วส่งต่อ <b>ชั้น 2</b> → <b>ชั้น 3</b> ตามที่ตั้งไว้
          จนครบทุกชั้นถึงจะถือว่าอนุมัติสมบูรณ์ · ถ้าตั้ง 1 ชั้น = อนุมัติจบที่หัวหน้า (เหมือนเดิม)
        </div>
      </div>
    </AdminLayout>
  );
}
